import cn from 'classnames';
import type { ReactNode } from 'react';
import { RichTextPreview } from '../../../article/components/RichTextEditor/RichTextPreview';

export type SectionHeaderProps = {
  /** Left-hand slot. A **string** is treated as the rich-text HTML the CMS stores and is
   * rendered through `RichTextPreview` — which is how every built-in component already
   * rendered its `title`, so existing page configs are unaffected. Any other node renders
   * as-is, which is how callers pass their own heading markup. */
  title?: ReactNode;
  /** Right-hand slot: a navigation link, or an action such as the stats table's pagination
   * button. Nav-vs-action is the caller's business; the header only positions it. */
  action?: ReactNode;
  className?: string;
};

/**
 * The shared "title left / link top-right" section row (SC-1143).
 *
 * Three pages had grown their own copy of this row — `club-profile__section-head`,
 * `match-detail__section-head`, and `team-overview__more-articles-btn-container` — while the
 * built-in components (League Table, Fixtures, Stats) rendered their optional link *underneath*
 * their content instead. This is the one definition all of them now share; the per-page classes
 * survive only as thin overrides for what genuinely differs between them (rule colour, spacing).
 *
 * Renders nothing at all when neither slot is filled, so a component that mounts it
 * unconditionally costs an unconfigured page no markup.
 */
export default function SectionHeader({ title, action, className }: SectionHeaderProps) {
  // Coerced rather than used as a bare `&&` guard: `ReactNode` admits `0`, and `{0 && <div/>}`
  // renders a stray "0" text node into the row. Falsy also covers the empty string a cleared
  // CMS rich-text field arrives as, and the `false` the `a && b && <Node/>` call sites produce.
  const hasTitle = Boolean(title);
  const hasAction = Boolean(action);

  if (!hasTitle && !hasAction) {
    return null;
  }

  return (
    <div className={cn('section-header', className)}>
      {hasTitle && (
        <div className="section-header__title">
          {typeof title === 'string' ? <RichTextPreview content={title} /> : title}
        </div>
      )}
      {hasAction && <div className="section-header__action">{action}</div>}
    </div>
  );
}
